import { distanceMeters } from '../utils/geo';
import { LatLng } from '../utils/types';

// One place label from the street tiles: a kaupunginosa ('suburb'), an osa-alue
// ('quarter' / 'neighbourhood'), with its label point and distance from the query.
export type NearbyPlaceLabel = {
  name: string;
  kind: string;
  lat: number;
  lng: number;
  distanceM: number;
};

// Ask the Tilequery API for the same labels the collector reads off the map.
// Rendered tiles only carry `settlement_subdivision` from a certain zoom, so a
// zoomed-out map returned nothing; this asks the tileset directly. `settlement`
// (the town or city itself) is accepted as a second tier because outside Finland
// subdivisions are sparse: Stockholm has two within 3 km where Helsinki has six,
// and a city row beats an empty list.
const SUBDIVISION_CLASS = 'settlement_subdivision';
const TILEQUERY_URL = 'https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery';

export const fetchPlaceLabels = async (
  center: LatLng,
  radiusM: number,
): Promise<NearbyPlaceLabel[] | null> => {
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  if (!token) return null;
  const url =
    `${TILEQUERY_URL}/${center.lng},${center.lat}.json` +
    `?radius=${Math.round(radiusM)}&limit=50&layers=place_label&access_token=${token}`;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = (await response.json()) as {
      features?: Array<{
        geometry?: { type?: string; coordinates?: [number, number] };
        properties?: Record<string, unknown>;
      }>;
    };
    const byName = new Map<string, NearbyPlaceLabel>();
    (data.features ?? []).forEach((feature) => {
      const coordinates = feature.geometry?.coordinates;
      if (feature.geometry?.type !== 'Point' || !coordinates) return;
      const properties = feature.properties ?? {};
      const featureClass = String(properties.class ?? '');
      if (featureClass !== SUBDIVISION_CLASS && featureClass !== 'settlement') return;
      const name = String(properties.name ?? '').trim();
      const kind = String(properties.type ?? '');
      if (!name || kind === 'block') return;
      const [lng, lat] = coordinates;
      const distanceM = distanceMeters(center, { lat, lng });
      const seen = byName.get(name);
      // A subdivision always wins over the city it sits in, whatever the
      // distances say: "Norrmalm" is the useful row, "Stockholm" is the filler.
      if (!seen || (seen.kind === 'settlement' && featureClass === SUBDIVISION_CLASS)) {
        byName.set(name, {
          name,
          kind: featureClass === SUBDIVISION_CLASS ? kind : 'settlement',
          lat,
          lng,
          distanceM,
        });
      }
    });
    const rows = Array.from(byName.values());
    const subdivisions = rows.filter((row) => row.kind !== 'settlement');
    // Cities only stand in when the area has no subdivisions of its own.
    const chosen = subdivisions.length > 0 ? subdivisions : rows;
    return chosen.sort((a, b) => a.distanceM - b.distanceM);
  } catch {
    return null;
  }
};

// How many district rows the empty search shows.
const DISTRICT_ROWS = 3;
// A label point this close counts as the district a spot is in: the user's
// location in the empty search, a clicked address-less building's title
// (JAA-417).
export const HERE_MAX_M = 600;

// The rows to show from the labels around a point: kaupunginosa-level labels
// ('suburb') first, nearest first; smaller quarters fill in only when there are
// too few of them.
export const pickDistrictRows = (labels: NearbyPlaceLabel[]): NearbyPlaceLabel[] => {
  const suburbs = labels.filter((l) => l.kind === 'suburb');
  const rest = labels.filter((l) => l.kind !== 'suburb');
  return [...suburbs, ...rest].slice(0, DISTRICT_ROWS);
};
